import escapeHtml from 'escape-html';
import parse from 'html-react-parser';

import {
  IAdminSupplementCreateNewPayload,
  IAdminSupplementInquiryDetails,
  IAdminSupplementInquiryResponseFormValues,
  IAdminSupplementRenewPayload,
} from '@interfaces/adminSupplements';
import { IUserEnteredSupplementDataWithComments } from '@interfaces/supplements';

export const convertAdminSupplementDetailsIntoFormValues = (
  data: IAdminSupplementInquiryDetails,
): IAdminSupplementInquiryResponseFormValues => ({
  name: data.name,
  comments: data.comments || '',
  companyName: data.supplementCompanyName || '',
  drugClassIds: data.drugClassIds || [],
  ingredients: data.ingredients || [],
  organizationId: data.organizationId,
  productLink: data.productLink || '',
  riskLevelId: data.riskLevelId,
  supplementCategoryId: data.supplementCategoryId,
  supplementCompanyId: data.supplementCompanyId || 0,
  supplementInquiryId: data.supplementInquiryId,
  supplementResponseId: data.supplementResponseId || 0,
});

export const encodeHtmlText = (value?: string | null) => {
  if (!value) return '';

  return escapeHtml(value);
};

export const convertPendingNewSupplementIntoFormValues = (
  data: IUserEnteredSupplementDataWithComments,
  supplementInquiryId?: number,
): Partial<IAdminSupplementInquiryResponseFormValues> => ({
  name: data.supplementName,
  companyName: data.companyName || '',
  supplementCategoryId: data.supplementCategoryId,
  productLink: data.linkToProductPage,
  drugClassIds: [],
  ingredients: [],
  supplementInquiryId,
  comments: data.comments || '',
});

export const getInitialValues = (
  adminSupplementDetails: IAdminSupplementInquiryDetails | null,
  pendingNewSupplement: IUserEnteredSupplementDataWithComments | null,
): Partial<IAdminSupplementInquiryResponseFormValues> => {
  const supplementInquiryId = adminSupplementDetails?.supplementInquiryId;
  if (pendingNewSupplement)
    return convertPendingNewSupplementIntoFormValues(pendingNewSupplement, supplementInquiryId);
  if (adminSupplementDetails)
    return convertAdminSupplementDetailsIntoFormValues(adminSupplementDetails);
  return {};
};

export const convertFormValuesToCreateNewPayload = (
  data: IAdminSupplementInquiryResponseFormValues,
): IAdminSupplementCreateNewPayload => ({
  supplementName: data.name || '',
  companyName: data.companyName || '',
  supplementCategoryId: data.supplementCategoryId || null,
  linkToProductPage: data.productLink || '',
  supplementInquiryId: data.supplementInquiryId || null,
  drugClassIds: data.drugClassIds,
  ingredientIds: data.ingredients?.map(({ id }) => id) || null,
  riskLevelId: data.riskLevelId || null,
  comments: data.comments || null,
});

export const convertFormValuesToRenewPayload = (
  data: IAdminSupplementInquiryResponseFormValues,
): IAdminSupplementRenewPayload => ({
  supplementResponseId: data.supplementResponseId || null,
  supplementCompanyId: data.supplementCompanyId,
  name: data.name || '',
  supplementCategoryId: data.supplementCategoryId || null,
  productLink: data.productLink || '',
  supplementInquiryId: data.supplementInquiryId || null,
  drugClassIds: data.drugClassIds,
  ingredientIds: data.ingredients?.map(({ id }) => id) || null,
  riskLevelId: data.riskLevelId,
  comments: encodeHtmlText(data.comments) || null,
});

export const validateForm = (values: IAdminSupplementInquiryResponseFormValues) => {
  const errors: Partial<Record<keyof IAdminSupplementInquiryResponseFormValues, string | boolean>> =
    {};

  if (!values.name) errors.name = true;
  if (!values.productLink) errors.productLink = true;

  return errors;
};
export const getParsedText = (value?: string | null) => {
  if (!value) return '';

  const parsedText = parse(value);
  return typeof parsedText === 'object' ? value : parsedText;
};
